import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, usePagination, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createCustomer, listCustomers } from "../../api/customersApi";
import type { CustomerOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { CustomFieldsEditor } from "../../components/CustomFieldsEditor";

const COLUMNS: DataTableColumn<CustomerOut, keyof CustomerOut>[] = [
  { key: "name", header: "Name", render: (r) => r.name },
  { key: "phone", header: "Phone", render: (r) => r.phone ?? "—" },
  { key: "email", header: "Email", render: (r) => r.email ?? "—" },
  { key: "location", header: "Location", render: (r) => r.location ?? "—" },
  { key: "tin", header: "TIN", render: (r) => r.tin ?? "—" },
  { key: "notes", header: "Notes", render: (r) => r.notes ?? "—" },
];

/** Customer directory — `GET/POST /customers` (§10.3). `Sale`/`InvoiceOrBill` can both
 * carry an optional `customer_id`; `InvoicesPage`'s create form uses this list to let an
 * accountant attach a real customer instead of only a free-text `contact_name`. */
export function CustomersPage() {
  const { ensureFreshToken } = useAuth();
  const customersState = useAsyncData(() => ensureFreshToken().then((token) => listCustomers(token)), []);
  const { pageData: customersPageData, pagination: customersPagination, setPage: setCustomersPage } = usePagination(
    customersState.data ?? [],
    25,
  );

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [tin, setTin] = useState("");
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createCustomer(token, {
        name,
        phone: phone || undefined,
        email: email || undefined,
        location: location || undefined,
        tin: tin || undefined,
        custom_fields: customFields,
        notes: notes || undefined,
      });
      setName("");
      setPhone("");
      setEmail("");
      setLocation("");
      setTin("");
      setCustomFields({});
      setNotes("");
      customersState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create customer.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Customers</h1>
          <p className="page__subtitle">Customer directory — attach real customers to invoices/bills instead of free text only.</p>
        </div>
      </div>

      {customersState.error ? <StatusBanner kind="error">{customersState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New customer</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input label="Location" value={location} onChange={(e) => setLocation(e.target.value)} />
            <Input label="Tax Identification Number (TIN)" value={tin} onChange={(e) => setTin(e.target.value)} />
            <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <CustomFieldsEditor value={customFields} onChange={setCustomFields} />
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create customer"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        {customersState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={COLUMNS}
            rows={customersPageData}
            getRowId={(row) => row.id}
            pagination={customersPagination}
            onPageChange={setCustomersPage}
            emptyMessage="No customers yet."
            caption="Customers"
          />
        )}
      </div>
    </div>
  );
}
